import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MarketSegment, ResearchDataState, Scenario, SwotAnalysisState, SwotItem, TamAnalysisState } from '../interface/ReasearchData';

const initialState: ResearchDataState = {
  swotAnalysis: {} as SwotAnalysisState,
  tamAnalysis: {} as TamAnalysisState,
  scenario: [],
  lastScenario: {
    name: 'Undefined Scenario',
    calcBase: 'prior',
    bull: [] as any,
    base: [] as any,
    bear: [] as any
  } as any,
  saved: {}
};

const ResearchDataSlice = createSlice({
  name: 'researchData',
  initialState,
  reducers: {
    setResearchData: (state: ResearchDataState, action: PayloadAction<ResearchDataState>) => {
      Object.assign(state, action.payload);
    },
    setSwotAnalysis: (state: ResearchDataState, action: PayloadAction<SwotAnalysisState>) => {
      state.swotAnalysis = action.payload;
    },
    setTamAnalysis: (state: ResearchDataState, action: PayloadAction<TamAnalysisState>) => {
      state.tamAnalysis = action.payload;
    },
    updateTamSegment: (state: ResearchDataState, action: PayloadAction<{ id: string; segment: MarketSegment }>) => {
      const { id, segment } = action.payload;
      const segmentIndex = state.tamAnalysis.marketSegments.findIndex(seg => seg.id === id);
      if (segmentIndex !== -1) {
        state.tamAnalysis.marketSegments[segmentIndex] = segment;
      }
    },
    deleteTamSegment: (state: ResearchDataState, action: PayloadAction<string>) => {
      const id = action.payload;
      state.tamAnalysis.marketSegments = state.tamAnalysis.marketSegments.filter(seg => seg.id !== id);
    },
    setTamSegment: (state: ResearchDataState, action: PayloadAction<MarketSegment>) => {
      const newSegment = action.payload;
      state.tamAnalysis.marketSegments.push(newSegment);
    },

    updateSwotItem: (state: ResearchDataState, action: PayloadAction<{ itemKey: keyof SwotAnalysisState; id: number; text: string }>) => {
      const { itemKey, id, text } = action.payload;
      if (state.swotAnalysis[itemKey]) {
        state.swotAnalysis[itemKey] = state.swotAnalysis[itemKey].map((item: SwotItem) =>
          item.id === id ? { ...item, text } : item
        );
      }
    },
    updateSwotItemTags: (state: ResearchDataState, action: PayloadAction<{ itemKey: keyof SwotAnalysisState; id: number; tags: string[] }>) => {
      const { itemKey, id, tags } = action.payload;
      if (state.swotAnalysis[itemKey]) {
        state.swotAnalysis[itemKey] = state.swotAnalysis[itemKey].map((item: SwotItem) =>
          item.id === id ? { ...item, tags } : item
        );
      }
    },
    addSwotItemTag: (state: ResearchDataState, action: PayloadAction<{ itemKey: keyof SwotAnalysisState; id: number; tag: string }>) => {
      const { itemKey, id, tag } = action.payload;
      if (state.swotAnalysis[itemKey]) {
        state.swotAnalysis[itemKey] = state.swotAnalysis[itemKey].map((item: SwotItem) =>
          item.id === id ? { ...item, tags: [...(item.tags || []), tag] } : item
        );
      }
    },
    removeSwotItemTag: (state: ResearchDataState, action: PayloadAction<{ itemKey: keyof SwotAnalysisState; id: number; tag: string }>) => {
      const { itemKey, id, tag } = action.payload;
      if (state.swotAnalysis[itemKey]) {
        state.swotAnalysis[itemKey] = state.swotAnalysis[itemKey].map((item: SwotItem) =>
          item.id === id ? { ...item, tags: (item.tags || []).filter(t => t !== tag) } : item
        );
      }
    },
    deleteSwotItem: (state: ResearchDataState, action: PayloadAction<{ itemKey: keyof SwotAnalysisState; id: number }>) => {
      const { itemKey, id } = action.payload;
      if (state.swotAnalysis[itemKey]) {
        state.swotAnalysis[itemKey] = state.swotAnalysis[itemKey].filter((item: SwotItem) => item.id !== id);
      }
    },
    setSwotItem: (state: ResearchDataState, action: PayloadAction<{ itemKey: keyof SwotAnalysisState; item: SwotItem }>) => {
      const { itemKey, item } = action.payload;
      if (state.swotAnalysis[itemKey]) {
        state.swotAnalysis[itemKey].push(item);
      } else {
        state.swotAnalysis[itemKey] = [item];
      }
    },
    setScenario: (state: ResearchDataState, action: PayloadAction<Scenario[]>) => {
      state.scenario = action.payload;
    },
    setLastScenario: (state: ResearchDataState, action: PayloadAction<Scenario>) => {
      state.lastScenario = action.payload;
    },
    updateLastScenarioName: (state: ResearchDataState, action: PayloadAction<string>) => {
      state.lastScenario!.name = action.payload;
    },
    updateLastScenarioCalcBase: (state: ResearchDataState, action: PayloadAction<string>) => {
      state.lastScenario!.calcBase = action.payload;
    },
    updateLastScenarioProjection: (state: ResearchDataState, action: PayloadAction<{ type: keyof Scenario; label: string; value: any }>) => {
      (state.lastScenario as any)[action.payload.type][action.payload.label] = action.payload.value;
    },
    updateLastScenarioItem: (
      state: ResearchDataState,
      action: PayloadAction<{ metric: string; year: string; value: string; type: keyof Scenario, label: string }>
    ) => {
      const { metric, year, value, type, label } = action.payload;
      if (state.lastScenario && (state.lastScenario as any)[type]) {
        (state.lastScenario as any)[type].data = (state.lastScenario as any)[type]?.data?.map((data: any) => {
          // if (data.fiscalYear === year) {
          if(data.yearLabel === label) {
            return { ...data, [metric]: value };
          }
          return data;
        });
      }
    }
  }
});

export const { setResearchData, setSwotAnalysis, updateSwotItem, updateSwotItemTags, addSwotItemTag, removeSwotItemTag, deleteSwotItem, setSwotItem, setTamAnalysis, setTamSegment, updateTamSegment, deleteTamSegment, setScenario, setLastScenario, updateLastScenarioName, updateLastScenarioItem, updateLastScenarioProjection, updateLastScenarioCalcBase } = ResearchDataSlice.actions;
export default ResearchDataSlice.reducer;

export const selectResearchData = (state: any): ResearchDataState => state.researchData;